<template>
  <b-card no-body class="mb-4">
    <template #header>
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <span><i class="fas fa-rectangle-ad mr-2"></i>จัดการโฆษณา</span>
        <div class="d-flex align-items-center">
          <b-form-checkbox
            switch
            class="mr-3 mb-0 ads-pause-switch"
            :checked="switchOn"
            :disabled="loading || pauseSaving"
            @change="onPauseToggle"
          >
            <span class="small">หยุดป๊อปอัปชั่วคราว</span>
            <b-spinner v-if="pauseSaving" small class="ml-1"></b-spinner>
          </b-form-checkbox>
          <b-button size="sm" variant="outline-secondary" class="mr-2" @click="load">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          </b-button>
          <b-button size="sm" variant="primary" @click="openEditor(null)">
            <i class="fas fa-plus mr-1"></i> สร้างโฆษณา
          </b-button>
        </div>
      </div>
    </template>

    <b-alert v-if="error" variant="danger" show class="m-3">{{ error }}</b-alert>

    <b-alert v-if="popupPaused" variant="warning" show class="m-3 mb-0">
      <i class="fas fa-pause-circle mr-1"></i>
      <strong>ป๊อปอัปโฆษณาถูกหยุดชั่วคราวทั้งระบบ</strong>
      ผู้ใช้จะไม่เห็นป๊อปอัป แต่แถบโฆษณายังแสดงตามปกติ
      <span v-if="popupPausedAt" class="text-muted">(ตั้งแต่ {{ formatDateTime(popupPausedAt) }})</span>
      — ปิดสวิตช์ด้านบนเพื่อกลับมาแสดงตามเดิม โดยโฆษณาแต่ละรายการยังคงตำแหน่งที่ตั้งไว้
    </b-alert>

    <b-table
      :items="ads"
      :fields="fields"
      :busy="loading"
      responsive
      hover
      show-empty
      empty-text="ยังไม่มีโฆษณาในระบบ"
      class="mb-0"
    >
      <template #cell(status)="row">
        <b-badge :variant="statusOf(row.item).variant">
          {{ statusOf(row.item).label }}
        </b-badge>
      </template>

      <template #cell(placement)="row">
        <b-badge v-if="row.item.showInCarousel" variant="light" class="mr-1">
          แถบโฆษณา
        </b-badge>
        <b-badge
          v-if="row.item.showInPopup"
          :variant="popupPaused ? 'warning' : 'light'"
          :title="popupPaused ? 'ป๊อปอัปถูกหยุดชั่วคราวทั้งระบบ' : ''"
        >
          ป๊อปอัป<span v-if="popupPaused"> (หยุดชั่วคราว)</span>
        </b-badge>
      </template>

      <template #cell(schedule)="row">
        <span v-if="row.item.startDt || row.item.endDt" class="small">
          {{ formatDate(row.item.startDt) || '—' }} →
          {{ formatDate(row.item.endDt) || '—' }}
        </span>
        <span v-else class="text-muted small">ตลอดเวลา</span>
      </template>

      <template #cell(stats)="row">
        <span v-if="statsFor(row.item)" class="small">
          <span :title="'จำนวนครั้งที่แสดง (' + rangeLabel + ')'">
            <i class="fas fa-eye text-muted mr-1"></i>{{ statsFor(row.item).impressions.toLocaleString() }}
          </span>
          <span class="ml-2" :title="'ผู้ใช้ที่เห็น (นับซ้ำได้รายวัน)'">
            <i class="fas fa-users text-muted mr-1"></i>{{ statsFor(row.item).dailyReach.toLocaleString() }}
          </span>
          <span class="ml-2" :title="'จำนวนคลิก'">
            <i class="fas fa-hand-pointer text-muted mr-1"></i>{{ statsFor(row.item).clicks.toLocaleString() }}
          </span>
        </span>
        <span v-else class="text-muted small">—</span>
      </template>

      <template #cell(actions)="row">
        <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEditor(row.item)">
          <i class="fas fa-pen"></i>
        </b-button>
        <b-button size="sm" variant="outline-danger" @click="confirmDelete(row.item)">
          <i class="fas fa-trash"></i>
        </b-button>
      </template>
    </b-table>

    <AdsEditorModal ref="editor" @saved="onSaved" />
  </b-card>
</template>

<script>
import AdsApiService from '@/services/AdsApiService';
import AdsEditorModal from '@/components/superadmin/AdsEditorModal.vue';

export default {
  name: 'AdsManagerTable',
  components: { AdsEditorModal },
  props: {
    /**
     * Report rows for the last 30 days, keyed by ad, so each row can show how
     * it is actually performing. Optional: the table is useful without them.
     */
    stats: {
      type: Array,
      default: () => []
    },
    rangeLabel: {
      type: String,
      default: '30 วันล่าสุด'
    }
  },
  data() {
    return {
      ads: [],
      loading: false,
      error: null,
      // System-wide popup takedown. Held here rather than per ad: it overrides
      // every row's popup placement without touching it, so resuming restores
      // exactly what was popping up before.
      popupPaused: false,
      popupPausedAt: null,
      pauseSaving: false,
      // What the switch is painted as, kept apart from `popupPaused` so the
      // banner and the row badges only ever describe what is actually live: the
      // click moves the switch, the server moves `popupPaused`.
      switchOn: false,
      fields: [
        { key: 'title', label: 'ชื่อโฆษณา' },
        { key: 'type', label: 'ประเภท' },
        { key: 'priority', label: 'ลำดับ' },
        { key: 'placement', label: 'ตำแหน่ง' },
        { key: 'schedule', label: 'ช่วงเวลา' },
        { key: 'status', label: 'สถานะ' },
        // Props are available in data(); computed properties are not.
        { key: 'stats', label: this.rangeLabel },
        { key: 'actions', label: '' }
      ]
    };
  },
  computed: {
    statsByAdId() {
      const map = new Map();
      for (const row of this.stats) map.set(row.adId, row);
      return map;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const response = await AdsApiService.listAds();
        const data = response?.data?.data || {};
        this.ads = data.ads || [];
        this.popupPaused = data.popupPaused === true;
        this.popupPausedAt = data.popupPausedAt || null;
        this.switchOn = this.popupPaused;
      } catch (error) {
        this.error =
          error?.response?.data?.message || 'โหลดรายการโฆษณาไม่สำเร็จ';
      } finally {
        this.loading = false;
      }
    },

    statsFor(ad) {
      return this.statsByAdId.get(ad.id) || null;
    },

    /**
     * "Active" alone would lie: an ad can be switched on and still be invisible
     * because its schedule has not started or has already ended.
     */
    statusOf(ad) {
      if (!ad.isActive) return { label: 'ปิดใช้งาน', variant: 'secondary' };

      // The popup takedown deliberately does NOT land here: a paused popup ad
      // is still live in the carousel, so the row keeps its real status and the
      // placement column is where the suspension is shown.

      const now = Date.now();
      if (ad.startDt && new Date(ad.startDt).getTime() > now) {
        return { label: 'รอเริ่ม', variant: 'info' };
      }
      if (ad.endDt && new Date(ad.endDt).getTime() < now) {
        return { label: 'หมดอายุ', variant: 'warning' };
      }
      return { label: 'กำลังแสดง', variant: 'success' };
    },

    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleDateString('th-TH');
    },

    formatDateTime(value) {
      if (!value) return '';
      return new Date(value).toLocaleString('th-TH', {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    },

    /**
     * Confirmed before it is sent, and only written to `popupPaused` once the
     * server has agreed: the switch is checked against the server state, so a
     * cancelled or failed flip leaves it showing what is actually live rather
     * than what was clicked.
     */
    async onPauseToggle(next) {
      // Set before the confirm, not after: on cancel the revert has to be a
      // real change to the bound value, or Vue batches both assignments into
      // one tick and the switch stays where the click left it.
      this.switchOn = next;

      const confirmed = await this.$bvModal.msgBoxConfirm(
        next
          ? 'ผู้ใช้ทุกสาขาจะไม่เห็นป๊อปอัปโฆษณาจนกว่าจะเปิดกลับ แถบโฆษณายังแสดงตามปกติ และโฆษณาแต่ละรายการยังคงตำแหน่งที่ตั้งไว้'
          : 'ป๊อปอัปจะกลับมาแสดงตามตำแหน่งและช่วงเวลาที่ตั้งไว้เดิม',
        {
          title: next ? 'หยุดป๊อปอัปชั่วคราว' : 'เปิดป๊อปอัปกลับ',
          okVariant: next ? 'warning' : 'primary',
          okTitle: next ? 'หยุดชั่วคราว' : 'เปิดกลับ',
          cancelTitle: 'ยกเลิก',
          centered: true
        }
      );

      if (!confirmed) {
        this.switchOn = this.popupPaused;
        return;
      }

      this.pauseSaving = true;
      this.error = null;
      try {
        const response = await AdsApiService.setPopupPaused(next);
        const data = response?.data?.data || {};
        this.popupPaused = data.paused === true;
        this.popupPausedAt = data.changedAt || null;
        this.$emit('changed');
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          (next ? 'หยุดป๊อปอัปไม่สำเร็จ' : 'เปิดป๊อปอัปกลับไม่สำเร็จ');
      } finally {
        this.switchOn = this.popupPaused;
        this.pauseSaving = false;
      }
    },

    openEditor(ad) {
      this.$refs.editor.show(ad);
    },

    onSaved() {
      this.load();
      this.$emit('changed');
    },

    async confirmDelete(ad) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `ต้องการเลิกใช้โฆษณา "${ad.title || ad.id}" ใช่หรือไม่? ข้อมูลสถิติที่เก็บไว้จะยังอยู่`,
        {
          title: 'ยืนยันการลบ',
          okVariant: 'danger',
          okTitle: 'เลิกใช้',
          cancelTitle: 'ยกเลิก',
          centered: true
        }
      );
      if (!confirmed) return;

      try {
        await AdsApiService.deleteAd(ad.id);
        this.load();
        this.$emit('changed');
      } catch (error) {
        this.error = error?.response?.data?.message || 'ลบโฆษณาไม่สำเร็จ';
      }
    }
  }
};
</script>
